import {inject, injectable} from 'inversify';

import type {BNSubtenantApiModel} from '../../../../api/types/bnrks/models';
import type {CoreSubtenantApiModel} from '../../../../api/types/core/models';
import type {Combine, Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {TenantMapperJrpc} from '../../Tenant/mappers/Tenant.mapper.jrpc';
import type {TenantModel} from '../../Tenant/Tenant.model';
import type {SubtenantModel} from '../Subtenant.model';

import {CoreServiceLocator} from '../../../system/serviceLocator/CoreServiceLocator';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {TENANT_MAPPER_JRPC_IDENTIFIER} from '../../Tenant/mappers/Tenant.mapper.jrpc.identifier';
import {TenantFactory} from '../../Tenant/Tenant.factory';
import {SubtenantFactory} from '../Subtenant.factory';
import {SubtenantExpandJrpc} from './Subtenant.expand.jrpc';
import {SubtenantArticlesAccessMapperJrpc} from './SubtenantArticlesAccess.mapper.jrpc';
import {SubtenantExtensionConfigurationMapperJrpc} from './SubtenantExtensionConfiguration.mapper.jrpc';

@injectable()
export class SubtenantMapperJrpc
  implements
    IApiDataMapper<
      SubtenantMapperJrpc.ApiData,
      SubtenantModel.Props,
      SubtenantModel
    >
{
  constructor(
    @inject(SubtenantFactory)
    private readonly factory: SubtenantFactory,
    @inject(SubtenantExpandJrpc)
    public readonly expand: SubtenantExpandJrpc,
    @inject(SubtenantExtensionConfigurationMapperJrpc)
    private readonly subtenantExtensionConfigurationMapper: SubtenantExtensionConfigurationMapperJrpc,
    @inject(SubtenantArticlesAccessMapperJrpc)
    private readonly subtenantArticlesAccessMapper: SubtenantArticlesAccessMapperJrpc,
    @inject(TenantFactory)
    private readonly tenantFactory: TenantFactory,
    @inject(DateTimeModelFactory)
    private readonly dateTimeFactory: DateTimeModelFactory,
    @inject(CoreServiceLocator) private readonly LOCATOR: CoreServiceLocator,
  ) {}

  mapDataToModel(
    apiData: SubtenantMapperJrpc.ApiData,
    tenant: TenantModel,
  ): SubtenantModel {
    const props = this.mapDataToProps(apiData);

    return this.factory.create({
      ...props,
      tenant,
    });
  }

  mapDataToProps(apiData: SubtenantMapperJrpc.ApiData): SubtenantModel.Props {
    return {
      articlesAccess: this.subtenantArticlesAccessMapper.mapDataToModel(
        apiData.subscribed_to,
      ),
      coachIds: apiData.coach_ids,
      colors: this.processColors(apiData),
      created: this.dateTimeFactory.createFromDateTimeObject(apiData.created),
      cromId: this.processCromId(apiData),
      email: this.processEmail(apiData),
      extensionConfiguration:
        this.subtenantExtensionConfigurationMapper.mapDataToModel(
          apiData.auto_restart_on,
        ),
      features: this.processFeatures(apiData),
      id: apiData._id,
      limits: this.processLimits(apiData),
      partnerId: apiData.partner_id,
      program: this.processProgram(apiData),
      session: this.processSession(apiData),
      share: this.processShare(apiData),
      tenant: this.tenantFactory.createEmpty(),
      title: apiData.title,
      updated: this.dateTimeFactory.createFromDateTimeObject(apiData.updated),
      urls: this.processUrls(apiData),
      userReview: this.processUserReview(apiData),
      widget: this.processWidget(apiData),
    };
  }

  mapExpandedToModel(apiData: SubtenantMapperJrpc.ExpandedApiData) {
    const props = this.mapDataToProps(apiData);

    if (apiData.tenant) {
      const tenantMapper: TenantMapperJrpc = this.LOCATOR.get(
        TENANT_MAPPER_JRPC_IDENTIFIER,
      );

      props.tenant = tenantMapper.mapDataToModel(apiData.tenant);
    }

    return this.factory.create(props);
  }

  mapExpandedToModelOrEmpty(
    apiData: Nullable<SubtenantMapperJrpc.ExpandedApiData>,
  ): SubtenantModel {
    if (!apiData) {
      return this.factory.createEmpty();
    }

    return this.mapExpandedToModel(apiData);
  }

  private processColors(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['colors'] {
    return {
      onboardingBg: apiData.colors.onboardingBg || '',
      primary: apiData.colors.primary || apiData.color || '',
    };
  }

  private processCromId(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['cromId'] {
    // Crom id is in 'croms' field on backend
    return apiData.forms['croms'];
  }

  private processEmail(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['email'] {
    return {
      sender: {
        email: apiData.sender_email || '',
        name: apiData.sender_name || '',
      },
      signature: {
        client: apiData.email_signature?.for_client || '',
        coach: apiData.email_signature?.for_coach || '',
      },
      templateFolder: apiData.template_folder || '',
    };
  }

  private processFeatures(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['features'] {
    return {
      autoMatching: apiData.features.auto_matching || apiData.auto_matching,
      digitalTwin: apiData.features.is_digital_twin_active,
      library: apiData.features.library,
      trial: {
        days: apiData.features.trial.days || 0,
        sessionsLimit: apiData.features.trial.sessions_limit || 0,
      },
      waitlist: apiData.limits.max_active_users !== null,
    };
  }

  private processLimits(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['limits'] {
    return {
      conferenceBooking: apiData.limits.conference_booking_days,
      maxActiveUsers: apiData.limits.max_active_users || 0,
      subgoalCreation: apiData.limits.subgoal_creation_days,
      taskCreation: apiData.limits.task_creation_days,
    };
  }

  private processProgram(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['program'] {
    return {
      duration:
        apiData.default_offer?.duration || apiData.program_duration || 0,
      title: apiData.default_offer?.title || apiData.program_title || '',
    };
  }

  private processSession(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['session'] {
    return {
      count:
        apiData.default_offer?.client_conferences.count ||
        apiData.session_count ||
        0,
      duration:
        apiData.default_offer?.client_conferences.duration ||
        apiData.session_duration ||
        0,
      extraLimit:
        apiData.default_offer?.client_conferences.extra_sessions_limit ||
        apiData.extra_sessions_limit ||
        0,
      intersessionBreak:
        apiData.default_offer?.client_conferences.intersession_break ||
        apiData.intersession_break ||
        0,
    };
  }

  private processShare(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['share'] {
    return {
      access: {
        description: apiData.share.access.description || '',
        link: apiData.share.access.link || '',
      },
    };
  }

  private processUrls(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['urls'] {
    return {
      faq: apiData.urls.faq || '',
      widget: apiData.urls.widget || '',
    };
  }

  private processUserReview(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['userReview'] {
    return {
      secret: apiData.user_review_secret || '',
      url: apiData.user_review_url || '',
    };
  }

  private processWidget(
    apiData: SubtenantMapperJrpc.ApiData,
  ): SubtenantModel.Props['widget'] {
    const widgetSettings = apiData.features.widget_flow;

    return {
      confirmation: widgetSettings?.required_confirmation
        ? {
            sendCodeCopy: widgetSettings.send_code_copy,
            type: widgetSettings.required_confirmation,
          }
        : null,
      isAccessible: Boolean(widgetSettings),
      uxType: widgetSettings?.ux_type ?? 'full',
    };
  }
}

export namespace SubtenantMapperJrpc {
  export type ApiData = Omit<
    Combine<CoreSubtenantApiModel, BNSubtenantApiModel>,
    'features' | 'limits'
  > & {
    features: Omit<
      Combine<
        CoreSubtenantApiModel['features'],
        BNSubtenantApiModel['features']
      >,
      'trial' | 'widget_flow'
    > & {
      trial: Combine<
        CoreSubtenantApiModel['features']['trial'],
        BNSubtenantApiModel['features']['trial']
      >;
      widget_flow?: CoreSubtenantApiModel['features']['widget_flow'];
    };
    limits: Combine<
      CoreSubtenantApiModel['limits'],
      BNSubtenantApiModel['limits']
    >;
  };

  export type ExpandedApiData = ApiData & SubtenantExpandJrpc.ExpandedFields;
}
